import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Paginator } from 'primereact/paginator';
import { getDataTableSortFields, getSearchParamSortFields } from '../../helpers/sortHelper';
import { RouteWithParams } from '../../helpers/routing';
import { Routes } from '../../constants/routes';
import {
	REPORT_URL_PARAMS,
	REPORT_IDS,
	DATETIME_FORMAT,
	TASK_LIST_VIEWS as VIEWS,
	GANTT_ZOOM_LEVELS,
	DATE_FORMAT,
	SortColumnsType,
} from '../../constants/constants';
import Icon from '../Common/Icon';
import Button from '../Common/Button';
import { daysToDaysWeeks } from '../../helpers/dateTime';
import Moment from 'moment';
import './BaselineTaskListDesktop.scss';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import { Checkbox } from 'primereact/checkbox';
import DatePicker from '../Common/DatePicker/DatePicker';
import PushdownPreviewDrawer from '../PushdownPreview/PushdownPreviewDrawer';
import BulkPushdownDrawer from './BulkPushdown/BulkPushdownDrawer';
import BaselineImportDrawer from './Import/BaselineImportDrawer';
import OrderTasksDrawer from './OrderTasksDrawer';
import BaselineTaskListColumnDrawer from './BaselineTaskListColumnDrawer';
import Dockbar from '../Common/Dockbar/Dockbar';
import Modal from '../Common/Modal/Modal';
import DateRangePickerWrapper from '../Common/DateRangePicker/DateRangePickerWrapper';
import ActiveToggleForm from './ActiveToggleForm';
import { Dropdown } from 'primereact/dropdown';
import Notification from '../Common/Notification';
import { TruncateText } from '../../helpers/strings';
import GanttChartView from './GanttChart/GanttChartView';
import OpeningDateModal from './GanttChart/OpeningDateModal';
import BulkUpdateAttributesDrawer from './BulkUpdate/BulkUpdateAttributesDrawer';
import Tag from '../Common/Tag/Tag';
import ReactGA from 'react-ga4'; // Updated to react-ga4
import { MultiSelect } from 'primereact/multiselect';
import { getMultiSelectFilterValues, getNullableOnChangeValue } from '../../helpers/multiselect';
import { classNames } from 'primereact/utils';
import { actions as userActions } from '../../actions/userActions';

// Initialize Google Analytics
const GOOGLE_TRACKING_ID = 'G-XXXXXXXXXX'; // Replace with your actual GA4 measurement ID
ReactGA.initialize(GOOGLE_TRACKING_ID);

class BaselineTaskListDesktop extends React.Component {
	constructor(props, context) {
		super(props, context);
		this.state = {
			cols: [],
			columnOptions: [],
			showPreviewDrawer: false,
			isBulkPushdownDrawerOpen: false,
			isBulkUpdateAttributesDrawerOpen: false,
			isColumnDrawerOpen: false,
			isImportDrawerOpen: false,
			isOrderTasksDrawerOpen: false,
			orderTasksTargetDaysOut: '',
			orderTasksTargetStaticDate: '',
			showDateRangePicker: false,
			openingDate: null,
			showOpeningDateModal: false,
			ganttZoomLevel: GANTT_ZOOM_LEVELS.Month.value,
			dropFilterChanged: false,
			dateRange: {
				start: null,
				end: null,
				name: '',
			},
			selectedTask: null,
			selectedRowIndex: null,
		};

		this.dataTableRef = React.createRef();
	}

	componentDidMount() {
		this.buildColumnOptions();
	}

	/**
	 * Jump through hoops to make sure we update the list of column options
	 * after we've finished loading the feature flags from the API.  By that
	 * time this component is already mounted and rendered.
	 */
	componentDidUpdate(prevProps) {
		const { isStartDateActive, isTaskStepsActive, isTaskEffortActive, isTaskFeedbackActive, isTaskDependenciesActive } = this.props.formFilters;

		if (
			isStartDateActive != prevProps.formFilters.isStartDateActive ||
			isTaskStepsActive != prevProps.formFilters.isTaskStepsActive ||
			isTaskEffortActive != prevProps.formFilters.isTaskEffortActive ||
			isTaskFeedbackActive != prevProps.formFilters.isTaskFeedbackActive ||
			isTaskDependenciesActive != prevProps.formFilters.isTaskDependenciesActive ||
			prevProps.columnOrder !== this.props.columnOrder
		) {
			this.buildColumnOptions();
		}

		if (this.state.selectedRowIndex !== null && this.dataTableRef.current !== null) {
			const targetRow = this.dataTableRef.current.getElement().querySelector(`.p-datatable-tbody > tr:nth-child(${this.state.selectedRowIndex + 1})`);
			if (targetRow) {
				targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}

	getStaticColumns = () => {
		return [
			{
				field: 'name',
				header: 'Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
				body: this.nameTemplate,
			},
			{
				field: 'startDaysOut',
				header: 'Start Weeks/Days Out',
				isFeatureEnabled: this.props.formFilters.isStartDateActive,
				body: this.startDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetDaysOut',
				header: 'Target Weeks/Days Out',
				isFeatureEnabled: true,
				body: this.targetDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'startStaticDate',
				header: 'Start Non-Adjusting Date',
				isFeatureEnabled: this.props.formFilters.isStartDateActive,
				body: this.startStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetStaticDate',
				header: 'Target Non-Adjusting Date',
				isFeatureEnabled: true,
				body: this.targetStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'ordinal',
				header: 'Task Order',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
			},
			{
				field: 'baselineJobTitle',
				header: 'Baseline Job Title',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'additionalInfo',
				header: 'Additional Information',
				isFeatureEnabled: true,
				default: true,
				body: this.additionalInfoTemplate,
				isSortable: true,
			},
			{
				field: 'links',
				header: 'Links',
				isFeatureEnabled: true,
				body: this.linksTemplate,
			},
			{
				field: 'department',
				header: 'Department Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'discipline',
				header: 'Discipline',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'community',
				header: 'Community',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'phase',
				header: 'Phase',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'anticipatedEffort',
				header: 'Anticipated Effort',
				isFeatureEnabled: this.props.formFilters.isTaskEffortActive,
				isSortable: true,
			},
			{
				field: 'totalDependencies',
				header: '# of Dependencies',
				isFeatureEnabled: this.props.formFilters.isTaskDependenciesActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'totalSubtasks',
				header: '# of Steps',
				isFeatureEnabled: this.props.formFilters.isTaskStepsActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'totalResolvedFeedback',
				header: '# of Resolved Feedback',
				isFeatureEnabled: this.props.formFilters.isTaskFeedbackActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'createdDate',
				header: 'Created Date',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: this.createdDateTemplate,
			},
			{
				field: 'modifiedDate',
				header: 'Last Modified',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: this.modifiedDateTemplate,
			},
			{
				field: 'isActive',
				header: 'Active',
				isFeatureEnabled: true,
				body: this.activeTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'tags',
				header: 'Tags',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: this.tagsTemplate,
			},
		];
	};

	buildColumnOptions = () => {
		const allColumns = this.getStaticColumns().filter((col) => col.isFeatureEnabled);
		const columnOrder = this.props.columnOrder;

		let activeColumns = [];

		if (columnOrder && columnOrder.length > 0) {
			activeColumns = columnOrder.map((field) => allColumns.find((col) => col.field === field)).filter((col) => !!col); // remove undefined
		} else {
			activeColumns = allColumns.filter((col) => col.default === true);
		}

		const columnOptions = allColumns.map((col) => ({
			label: col.header,
			value: col,
		}));

		this.setState({
			cols: activeColumns,
			columnOptions,
		});
	};

	onBaselineSelected = (id) => {
		this.props.updateParamsAndSearch('baselineId', id);
	};

	/**
	 * Helper method to detect if the click event originated from a checkbox element
	 * @param {Event} originalEvent - The original DOM event
	 * @returns {boolean} - True if the click was on a checkbox element
	 */
	isCheckboxClick = (originalEvent) => {
		return (
			originalEvent.target.innerHTML.indexOf('p-checkbox-box') >= 0 ||
			originalEvent.target.className.indexOf('p-checkbox-box') >= 0 ||
			originalEvent.target.className.indexOf('p-checkbox-icon') >= 0 ||
			originalEvent.target.closest('.p-checkbox') !== null
		);
	};

	onSelectTask = (e) => {
		if (this.isCheckboxClick(e.originalEvent)) {
			const baselineTaskId = e.value.id;
			this.props.onToggleCheckedTask(baselineTaskId);
		}
	};

	onRowClick = (e) => {
		// Handle row clicks directly to ensure we can re-select the same task
		if (!this.isCheckboxClick(e.originalEvent)) {
			const baselineTaskId = e.data.id;
			const rowIndex = this.props.baselineTasks.indexOf(e.data);
			
			// Update selection state and always call showTask to handle re-selection
			this.setState({ 
				selectedTask: e.data, 
				selectedRowIndex: rowIndex 
			});
			
			// Always call showTask to handle both new selections and re-selections
			this.props.showTask(baselineTaskId);
		}
	};

	showTask = () => {
		this.props.showTask(null);
	};

	milestoneTemplate = (rowData) => {
		if (rowData.isMilestone) {
			return (
				<div className='text-warning milestone' title='Milestone Task'>
					&#x25C6;
				</div>
			);
		}
	};

	activeTemplate = (rowData) => {
		if (rowData.isActive) {
			return (
				<div className='text-success text-center'>
					<Icon name='check-circle' />
				</div>
			);
		} else {
			return (
				<div className='text-danger text-center'>
					<Icon name='times-circle' />
				</div>
			);
		}
	};

	onToggleCheckedTask = (baselineTask) => {
		this.props.onToggleCheckedTask(baselineTask.id);
	};

	checkboxTemplate = (rowData) => {
		return <Checkbox name='checkbox' onChange={() => this.onToggleCheckedTask(rowData)} checked={rowData.IsChecked}></Checkbox>;
	};

	onMasterCheckboxToggle = () => {
		this.props.onMasterCheckboxToggle();
	};

	startDaysOutTemplate = (rowData) => {
		return daysToDaysWeeks(rowData.startDaysOut);
	};

	startStaticDateTemplate = (rowData) => {
		if (rowData.startStaticDate) {
			return Moment.utc(rowData.startStaticDate).format(DATE_FORMAT);
		}

		return '';
	};

	targetDaysOutTemplate = (rowData) => {
		return daysToDaysWeeks(rowData.targetDaysOut);
	};

	targetStaticDateTemplate = (rowData) => {
		if (rowData.targetStaticDate) {
			return Moment.utc(rowData.targetStaticDate).format(DATE_FORMAT);
		} else {
			return null;
		}
	};

	createdDateTemplate = (rowData) => {
		return Moment.utc(rowData.createdDate).format(DATETIME_FORMAT);
	};

	modifiedDateTemplate = (rowData) => {
		return Moment.utc(rowData.modifiedDate).format(DATETIME_FORMAT);
	};

	linksTemplate = (rowData) => {
		let retval = [];

		if (rowData.links && rowData.links.length > 0) {
			for (let i = 0; i < rowData.links.length; i++) {
				const link = rowData.links[i];

				if (link.customerFileId) {
					retval.push(
						<button key={link.ordinal} type='button' className='btn btn-link' onClick={(e) => this.onDownloadLink(link, e)}>
							{link.name}
						</button>
					);
				} else {
					let protocol = '';
					const linkUrl = link.url.toLowerCase();
					if (!linkUrl.startsWith('http')) protocol = 'http://';

					retval.push(
						<div key={link.ordinal}>
							<a href={protocol + link.url} rel='noopener noreferrer' target='_blank'>
								{link.name}
							</a>
						</div>
					);
				}
			}
		}

		return retval;
	};

	onDownloadLink = async (link, e) => {
		e.preventDefault();
		try {
			// extract the file name from the link.url
			let start = link.url.indexOf('=') + 1;
			let fileName = link.url.substr(start, link.url.length - start);

			await this.props.filesActions.download(fileName);
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	nameTemplate = (rowData) => {
		return <div className='show-newlines'>{rowData.name}</div>;
	};

	additionalInfoTemplate = (rowData) => {
		return <div className='show-newlines'>{TruncateText(rowData.additionalInfo, 200)}</div>;
	};

	tagsTemplate = (rowData) => {
		const retval = [];

		if (rowData.tags && rowData.tags.length > 0) {
			for (let i = 0; i < rowData.tags.length; i++) {
				const tag = rowData.tags[i];

				retval.push(
					<div className='mb-1' key={tag.name}>
						<Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
					</div>
				);
			}
		}
		return retval;
	};

	onTextFilterChange = (e) => {
		this.props.updateSearchParams(e.target.name, e.target.value);
	};

	onSearchBlur = () => {
		this.props.updateParamsAndSearch();
	};

	keyUpFilter = (e) => {
		if (e.keyCode == 13) {
			this.props.updateParamsAndSearch();
		}
	};

	onActiveChange = (e) => {
		const selectedValue = getNullableOnChangeValue(e);

		this.props.updateParamsAndSearch('isActive', selectedValue);
	};

	onPreviewPushdown = (open) => {
		this.setState({ showPreviewDrawer: open });
	};

	onBulkPushdown = () => {
		this.setState({ isBulkPushdownDrawerOpen: true });
	};

	onBulkPushdownComplete = () => {
		this.setState({ isBulkPushdownDrawerOpen: false });
		this.props.onBulkPushdownComplete();
	};

	onBulkPushdownCancel = () => {
		this.setState({ isBulkPushdownDrawerOpen: false });
	};

	onBulkUpdateAttributes = () => {
		this.setState({ isBulkUpdateAttributesDrawerOpen: true });
	};

	onBulkUpdateAttributesComplete = () => {
		this.props.onBulkPushdownComplete();
		this.setState({ isBulkUpdateAttributesDrawerOpen: false });
	};

	onBulkUpdateAttributesCancel = () => {
		this.setState({ isBulkUpdateAttributesDrawerOpen: false });
	};

	onImportClick = () => {
		this.setState({ isImportDrawerOpen: true });
	};

	onColumnsClick = () => {
		this.setState({ isColumnDrawerOpen: true });
	};

	onColumnsClose = () => {
		this.setState({ isColumnDrawerOpen: false });
	};

	onColumnToggle = (name, value) => {
		let columns = [...this.state.cols];
		let modifiedColumn = this.state.columnOptions.find((m) => m.value.field === name);

		if (!modifiedColumn) return;

		if (value) {
			if (!columns.some((col) => col.field === name)) {
				columns.push(modifiedColumn.value);
			}
		} else {
			columns = columns.filter((col) => col.field !== name);
		}

		const orderedColumnFields = ['isChecked', ...columns.map((col) => col.field)];

		this.setState({ cols: columns }, async () => {
			await this.props.userActions.saveUsersColumns({ columns: orderedColumnFields, columnsType: SortColumnsType.AdminPageBaselineTasks });
			this.props.onColumnOrderChange(orderedColumnFields);
		});
	};

	onExportClick = () => {
		const params = [
			{
				key: REPORT_URL_PARAMS.BaselineId.toString(),
				value: this.props.searchParams.baselineId,
			},
		];
		const route = Routes.Reports + '/' + REPORT_IDS.BaselineTaskList;
		RouteWithParams(route, params, false);
	};

	onImportCancel = () => {
		this.setState({ isImportDrawerOpen: false });
	};

	onImportComplete = () => {
		this.props.updateParamsAndSearch();
		this.setState({ isImportDrawerOpen: false });
	};

	listingRefresh = () => {
		this.props.onComplete();
	};

	showDateRangePicker = (rangeName) => {
		let start = null;
		let end = null;

		start = this.props.searchParams.createdDateStart ? this.props.searchParams.createdDateStart : null;
		end = this.props.searchParams.createdDateEnd ? this.props.searchParams.createdDateEnd : null;

		if (typeof end === 'string') {
			end = Moment.utc(Date.parse(end));
		}
		if (typeof start === 'string') {
			start = Moment.utc(Date.parse(start));
		}

		const newDateRange = {
			...this.state.dateRange,
			name: rangeName,
			start: start,
			end: end,
		};
		this.setState({
			showDateRangePicker: true,
			dateRange: newDateRange,
		});
	};

	// the event that fires when a new date in the range picker is selected
	// remember these so we can add to search params if they hit the Apply button on the modal
	onStartRangeChange = (startDate, endDate) => {
		const newDateRange = {
			...this.state.dateRange,
			start: startDate,
			end: endDate,
		};
		this.setState({ dateRange: newDateRange });
	};

	// the event that fires when the Apply button is clicked in a date range modal
	onSelectDateRange = async () => {
		this.setState({ showDateRangePicker: false });
		// const start = Moment(this.state.dateRange.start).format(DATE_FORMAT);
		// const end = Moment(this.state.dateRange.end).format(DATE_FORMAT);
		const start = Moment.utc(this.state.dateRange.start);
		const end = Moment.utc(this.state.dateRange.end);
		this.props.updateCreatedDateParams(start, end);
	};

	onCancelDateRange = () => {
		this.setState({ showDateRangePicker: false });
	};

	onChangeOpeningDate = (newOpeningDate) => {
		this.setState({ openingDate: newOpeningDate });
	};

	handleMultiSelectChange = (key) => (e) => {
		this.props.updateSearchParams(key, getMultiSelectFilterValues(e.value));
		this.setState({ dropFilterChanged: true });
	};

	handleMultiSelectClose = () => {
		if (this.state.dropFilterChanged === true) {
			this.props.updateParamsAndSearch();
			this.setState({ dropFilterChanged: false });
		}
	};

	customCloseIcon = () => {
		return (
			<i
				className={classNames('pi', {
					'pi-times': !this.state?.dropFilterChanged,
					'pi-check-square text-success': this.state?.dropFilterChanged,
				})}
			/>
		);
	};

	getColumnFilterMultiSelectValues = (sourceIds, columnFiltersValues) => {
		let results = [];

		if (!sourceIds || sourceIds.length === 0) return [];

		if (columnFiltersValues && columnFiltersValues.length > 0) {
			for (let i = 0; i < sourceIds.length; i++) {
				const value = columnFiltersValues.find((m) => m.id.toString() === sourceIds[i].toString());
				if (value) {
					results.push(value);
				}
			}
		}

		return results;
	};

	renderColumns = () => {
		const activeOptions = [
			{ label: 'Yes', value: true },
			{ label: 'No', value: false },
			{ label: 'All', value: null },
		];

		const baselineJobTitlesValues = this.getColumnFilterMultiSelectValues(
			this.props.searchParams.baselineJobTitles,
			this.props.baselineJobTitles
		);
		const departmentValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.departments, this.props.departments);
		const communitiesValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.communities, this.props.communities);
		const phasesValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.phases, this.props.phases);
		const disciplinesValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.disciplines, this.props.disciplines);
		const tagsValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.tags, this.props.tags);

		const filters = {
			name: (
				<input
					type='text'
					name='name'
					className='p-inputtext'
					size='50'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.name}
					placeholder='Name'
				/>
			),
			additionalInfo: (
				<input
					type='text'
					name='additionalInfo'
					className='p-inputtext'
					size='50'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.additionalInfo}
					placeholder='Additional Info'
				/>
			),
			department: (
				<MultiSelect
					value={departmentValues}
					onChange={this.handleMultiSelectChange('departments')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.departments}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
			baselineJobTitle: (
				<MultiSelect
					value={baselineJobTitlesValues}
					onChange={this.handleMultiSelectChange('baselineJobTitles')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.baselineJobTitles}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
			community: (
				<MultiSelect
					value={communitiesValues}
					onChange={this.handleMultiSelectChange('communities')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.communities}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
			phase: (
				<MultiSelect
					value={phasesValues}
					onChange={this.handleMultiSelectChange('phases')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.phases}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
			discipline: (
				<MultiSelect
					value={disciplinesValues}
					onChange={this.handleMultiSelectChange('disciplines')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.disciplines}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
			isActive: (
				<Dropdown
					className='ui-column-filter'
					value={this.props.searchParams.isActive}
					options={activeOptions}
					optionLabel='label'
					optionValue='value'
					onChange={this.onActiveChange}
					placeholder='Select an option'
				/>
			),
			createdDate: (
				<Button
					type='button'
					bootstrapClass='light ui-column-filter'
					name='createdDateRangeButton'
					text={this.props.searchParams.createdDateStart || this.props.searchParams.createdDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => this.showDateRangePicker('createdDate')}
				/>
			),
			tags: (
				<MultiSelect
					value={tagsValues}
					onChange={this.handleMultiSelectChange('tags')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.tags}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={this.customCloseIcon()}
				/>
			),
		};

		let columns = this.state.cols.map((col) => {
			const filterElement = filters[col.field];
			// HACK: (jwv) PrimeReact's MultiSelect is throwing an error any time we deselect a column from the column selector
			// when it's internal object comparison code tries to set the _$visible property on a style object defined in our
			// columns objects.  IsActive was the only column with style object, and it was setting the width to 80px, so this
			// hack is in place to explicitly set that on the column here, instead of definining the style object as a member
			// of the column definition.
			if (col.field === 'isActive') {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
						style={{ width: '80px' }}
					/>
				);
			} else if (filterElement && col.isFeatureEnabled) {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
					/>
				);
			} else if (col.isFeatureEnabled) {
				return <Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} />;
			} else {
				return null;
			}
		});

		return columns;
	};

	onSort = ({ sortField, sortOrder }) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		
		this.setState(
			{
				selectedRowIndex: null,	// Reset selected row index & task
				selectedTask: null
			},
			() => this.props.onSort(sortBy, sortDir)
		);
	};

	onPageChange = (e) => {
		this.setState(
			{
				selectedRowIndex: null,	// Reset selected row index & task
				selectedTask: null
			},
			() => this.props.updateParamsAndSearch('pageNumber', e.page + 1)
		);
	};

	onOrderTasksClick = () => {
		const tasks = this.props.checkedTasks.slice();
		const targetDaysOut = tasks[0].targetDaysOut;
		const targetStaticDate = tasks[0].targetStaticDate;
		if (tasks.length > 0) {
			for (let i = 1; i < tasks.length; i++) {
				const task = tasks[i];
				const invalidDaysOut = task.targetDaysOut !== targetDaysOut;
				const invalidStaticDate = task.targetStaticDate !== targetStaticDate;
				if (invalidDaysOut || invalidStaticDate) {
					Notification(
						'error',
						'Error',
						'To use the Order Tasks feature, the selected tasks must be on the same Weeks/Days Out. Please reselect one or more tasks.'
					);
					return;
				}
			}
		}

		this.setState({
			isOrderTasksDrawerOpen: true,
			orderTasksTargetDaysOut: targetDaysOut,
			orderTasksTargetStaticDate: targetStaticDate
		});
	};

	onClearCheckedTasks = () => {
		if (this.props.allTasksChecked) {
			// If all tasks are checked, toggle will uncheck all
			this.props.onMasterCheckboxToggle();
		}
	};

	onOrderTasksComplete = () => {
		this.onClearCheckedTasks();
		this.setState(
			{
				isOrderTasksDrawerOpen: false,
				selectedRowIndex: null,	// Reset selected row index & task
				selectedTask: null
			},
			() => this.props.updateParamsAndSearch()
		);
	};

	onOrderTasksCancel = () => {
		this.setState({ isOrderTasksDrawerOpen: false });
	};

	onCancelOpeningDateModal = () => {
		this.setState({ showOpeningDateModal: false });
	};

	onCompleteOpeningDateModal = (openingDate) => {
		ReactGA.event({ category: 'Baseline Task List Page', action: 'Toggle Gantt Chart' });
		this.setState({ openingDate: openingDate, showOpeningDateModal: false }, this.props.onViewSelectorChange(VIEWS.Gantt.value));
	};

	onGanttZoomIn = () => {
		if (this.state.ganttZoomLevel < GANTT_ZOOM_LEVELS.Day.value) {
			this.setState({ ganttZoomLevel: this.state.ganttZoomLevel + 1 });
		}
	};

	onGanttZoomOut = () => {
		if (this.state.ganttZoomLevel > GANTT_ZOOM_LEVELS.Month.value) {
			this.setState({ ganttZoomLevel: this.state.ganttZoomLevel - 1 });
		}
	};

	onViewChange = (viewName) => {
		if (viewName === VIEWS.List.value) {
			// do nothing
		} else if (viewName === VIEWS.Gantt.value) {
			if (this.state.openingDate === null) {
				this.setState({ showOpeningDateModal: true });
				return; // return because we don't want to switch views until they select an opening date (they could cancel)
			}
		}

		this.props.onViewSelectorChange(viewName);
	};

	onColumnReorder = async (e) => {
		const reorderedFields = e.columns.map((c) => c.props.field);

		const filtered = reorderedFields.filter((f) => f !== 'isChecked');

		const corrected = ['isChecked', ...filtered];

		await this.props.userActions.saveUsersColumns({ columns: corrected, columnsType: SortColumnsType.AdminPageBaselineTasks });
		this.props.onColumnOrderChange(corrected);
	};

	rowClass = (data) => {
        return {
            'p-highlight': data.id === this.state.selectedTask?.id,
        };
    };

	render() {
		const { sortField, sortOrder } = getDataTableSortFields(this.props.searchParams);

		const viewOptions = [
			{ id: VIEWS.List.value, name: VIEWS.List.label, icon: VIEWS.List.icon },
			{ id: VIEWS.Gantt.value, name: VIEWS.Gantt.label, icon: VIEWS.Gantt.icon },
		];

		const masterCheckbox = (
			<div>
				<Checkbox name='checkbox' onChange={() => this.onMasterCheckboxToggle()} checked={this.props.allTasksChecked}></Checkbox>
			</div>
		);

		const filterButtonClass = this.props.hasNoFilters() ? '' : ' text-primary';

		const isBulkMode = this.props.checkedTasks && this.props.checkedTasks.length > 0;

		const toolbarCols = this.props.selectedView === VIEWS.Gantt.value ? ' col-md-7' : ' col-md-8';
		return (
			<div className='task-list-wrapper'>
				<LoadingIndicator isLoading={this.props.isFetchingBaselines}>
					<div className='task-list-header row'>
						<div className='col-md-4'>
							<div className='float-start w-100'>
								<MultiSelectBox
									name='baselineId'
									label='Baseline'
									idField='id'
									valueField='name'
									options={this.props.baselines}
									onChange={this.onBaselineSelected}
									selectedValues={this.props.searchParams.baselineId.toString()}
									isRequired={true}
									errorMessage='Baseline is required'
									isMulti={false}
								/>
							</div>
						</div>
						{this.props.selectedView === VIEWS.Gantt.value && (
							<div className='col-md-1'>
								<DatePicker
									showIcon
									className={'opening-date-picker'}
									value={this.state.openingDate}
									name='openingDate'
									label='Opening Date'
									onChange={this.onChangeOpeningDate}
								/>
							</div>
						)}
						{this.props.searchParams.baselineId > 0 ? (
							<div className={'task-list-toolbar' + toolbarCols}>
								{isBulkMode && (
									<Fragment>
										<div className='icon' onClick={this.onBulkUpdateAttributes}>
											<Icon name='pencil-alt' />
											Update Attributes
										</div>
										<div className='icon' onClick={this.onBulkPushdown}>
											<Icon name='tasks' />
											Bulk Pushdown
										</div>
										<div className='icon' onClick={() => this.onPreviewPushdown(true)}>
											<Icon name='tasks' />
											Preview Pushdown
										</div>
										<div className='icon' onClick={this.onOrderTasksClick}>
											<Icon name='arrows-v' />
											Order Tasks
										</div>
									</Fragment>
								)}
								{!isBulkMode && (
									<Fragment>
										<MultiSelectBox
											name='views'
											idField='id'
											valueField='name'
											className='view-selector'
											options={viewOptions}
											onChange={this.onViewChange}
											selectedValues={this.props.selectedView}
											isMulti={false}
											isSearchable={false}
										/>
										<div className='d-inline'>
											<Icon name='filter' />
											<div className={'show-filters' + filterButtonClass} onClick={this.props.onViewFiltersClick}>
												Show Filters
											</div>
											<div className='d-inline'>|</div>
											<div className={'clear-filters' + filterButtonClass} onClick={this.props.clearFilters}>
												Clear Filters
											</div>
										</div>
										{this.props.selectedView != VIEWS.Gantt.value ? (
											<Fragment>
												<div className='icon' onClick={this.onColumnsClick}>
													<Icon name='eye' /> Columns
												</div>
												<div className='icon' onClick={this.onExportClick}>
													<Icon name='file-excel' />
													Export
												</div>
												<div className='icon' onClick={this.onImportClick}>
													<Icon name='file-code' />
													Import
												</div>
											</Fragment>
										) : (
											<div className='d-inline'>
												<div className='icon zoom-in' onClick={this.onGanttZoomIn}>
													<Icon name='search-plus' stylePrefix='far' />
													Zoom In
												</div>
												<div className='icon zoom-out' onClick={this.onGanttZoomOut}>
													<Icon name='search-minus' stylePrefix='far' />
													Zoom Out
												</div>
											</div>
										)}
									</Fragment>
								)}
								<button type='button' className='btn btn-primary' onClick={this.showTask} alt='Add Baseline Task'>
									<span className='me-2'>Add Baseline Task</span>
									<Icon name='plus' />
								</button>
							</div>
						) : (
							''
						)}
					</div>

					{this.props.selectedView === VIEWS.List.value && this.props.searchParams.baselineId > 0 && (
						<LoadingIndicator isLoading={this.props.isFetchingTasks || this.props.isFetchingFilters} hideWhileLoading={true}>
							<div className='task-list-results-table'>
								<DataTable
									ref={this.dataTableRef}
									dataKey='id'
									filterDisplay='row'
									className='p-datatable-sm p-datatable-striped'
									value={this.props.baselineTasks}
									selectionMode='single'
									selection={this.state.selectedTask}
									onSelectionChange={this.onSelectTask}
									onRowClick={this.onRowClick}
									rowClassName={this.rowClass}
									reorderableColumns
									onColReorder={(e) => this.onColumnReorder(e)}
									sortField={sortField}
									sortOrder={sortOrder}
									onSort={this.onSort}>
									<Column
										field='isChecked'
										filter={true}
										filterElement={masterCheckbox}
										body={this.checkboxTemplate}
										style={{ textAlign: 'center', width: '40px' }}
									/>
									<Column field='isMilestone' body={this.milestoneTemplate} style={{ textAlign: 'center', width: '40px' }} />
									{this.renderColumns()}
								</DataTable>
								{this.props.totalCount > this.props.searchParams.pageSize && (
									<Paginator
										rows={this.props.searchParams.pageSize}
										totalRecords={this.props.totalCount}
										first={(this.props.searchParams.pageNumber - 1) * this.props.searchParams.pageSize}
										onPageChange={this.onPageChange}
									/>
								)}
								<div className='results-count'>Total records: {this.props.totalCount}</div>
							</div>
						</LoadingIndicator>
					)}
					{this.props.selectedView === VIEWS.Gantt.value && (
						<GanttChartView
							subscriptionId={this.props.subscriptionId}
							isFetchingTasks={this.props.isFetchingTasks}
							onSelectTask={this.props.showTask}
							baselineTaskActions={this.props.baselineTaskActions}
							searchParams={this.props.searchParams}
							lastFilterChange={this.props.lastFilterChange}
							openingDate={this.state.openingDate}
							zoomLevel={this.state.ganttZoomLevel}
							isTaskDependenciesActive={this.props.formFilters.isTaskDependenciesActive}
							isStartDateActive={this.props.formFilters.isStartDateActive}
						/>
					)}
				</LoadingIndicator>

				{this.state.showPreviewDrawer ? (
					<PushdownPreviewDrawer
						onComplete={() => this.onPreviewPushdown(false)}
						baselineId={this.props.searchParams.baselineId}
						isProjectWidgetActive={this.props.formFilters.isProjectWidgetActive}
					/>
				) : (
					''
				)}

				{this.state.isBulkPushdownDrawerOpen ? (
					<BulkPushdownDrawer
						baselineId={this.props.searchParams.baselineId}
						checkedTasks={this.props.checkedTasks}
						onComplete={this.onBulkPushdownComplete}
						onCancel={this.onBulkPushdownCancel}
						baselineTaskActions={this.props.baselineTaskActions}
					/>
				) : null}
				{this.state.isBulkUpdateAttributesDrawerOpen && (
					<BulkUpdateAttributesDrawer
						baselineId={this.props.searchParams.baselineId}
						checkedTasks={this.props.checkedTasks}
						onComplete={this.onBulkUpdateAttributesComplete}
						onCancel={this.onBulkUpdateAttributesCancel}
						baselineTaskActions={this.props.baselineTaskActions}
						departments={this.props.departments}
						jobTitles={this.props.baselineJobTitles}
						communities={this.props.communities}
						phases={this.props.phases}
						taskTags={this.props.tags}
						taskTagActions={this.props.taskTagActions}
					/>
				)}
				{this.state.isImportDrawerOpen ? (
					<BaselineImportDrawer
						subscriptionId={this.props.subscriptionId}
						baselineId={this.props.searchParams.baselineId}
						onCancel={this.onImportCancel}
						onComplete={this.onImportComplete}
						baselineActions={this.props.baselineActions}
					/>
				) : null}
				{this.state.isOrderTasksDrawerOpen && (
					<OrderTasksDrawer
						subscriptionId={this.props.subscriptionId}
						baselineId={this.props.searchParams.baselineId}
						targetDaysOut={this.state.orderTasksTargetDaysOut?.toString()}
						targetStaticDate={this.state.orderTasksTargetStaticDate}
						onCancel={this.onOrderTasksCancel}
						onComplete={this.onOrderTasksComplete}
						baselineTaskActions={this.props.baselineTaskActions}
					/>
				)}
				{isBulkMode && (
					<Dockbar>
						<ActiveToggleForm
							tasksChecked={this.props.baselineTasks.filter((bt) => bt.IsChecked === true)}
							baselineTaskActions={this.props.baselineTaskActions}
							listingRefresh={this.listingRefresh}
						/>
					</Dockbar>
				)}
				{this.state.isColumnDrawerOpen && (
					<BaselineTaskListColumnDrawer
						onComplete={this.onColumnsClose}
						onColumnToggle={this.onColumnToggle}
						columnOptions={this.state.columnOptions}
						columns={this.state.cols}
					/>
				)}
				{this.state.showDateRangePicker ? (
					<Modal
						id='startDateRangeModal'
						title='Select Date Range'
						body={
							<div className='datepicker-container'>
								<DateRangePickerWrapper
									onDatesChange={this.onStartRangeChange}
									initialStartDate={this.state.dateRange.start}
									initialEndDate={this.state.dateRange.end}
								/>
							</div>
						}
						primaryButtonText='Apply'
						onPrimaryClick={this.onSelectDateRange}
						onSecondaryClick={this.onCancelDateRange}
						onCloseClick={this.onCancelDateRange}
					/>
				) : null}
				{this.state.showOpeningDateModal && (
					<OpeningDateModal onCancel={this.onCancelOpeningDateModal} onComplete={this.onCompleteOpeningDateModal} />
				)}
			</div>
		);
	}
}

BaselineTaskListDesktop.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	baselines: PropTypes.array.isRequired,
	columnOrder: PropTypes.array.isRequired,
	onColumnOrderChange: PropTypes.func.isRequired,
	baselineActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	baselineTasks: PropTypes.array.isRequired,
	baselineTaskActions: PropTypes.object.isRequired,
	taskTagActions: PropTypes.object.isRequired,
	totalCount: PropTypes.number.isRequired,
	isFetchingBaselines: PropTypes.bool.isRequired,
	isFetchingTasks: PropTypes.bool.isRequired,
	isFetchingFilters: PropTypes.bool.isRequired,
	updateSearchParams: PropTypes.func.isRequired,
	updateParamsAndSearch: PropTypes.func.isRequired,
	searchParams: PropTypes.object.isRequired,
	departments: PropTypes.array.isRequired,
	baselineJobTitles: PropTypes.array.isRequired,
	communities: PropTypes.array.isRequired,
	phases: PropTypes.array.isRequired,
	disciplines: PropTypes.array.isRequired,
	tags: PropTypes.array.isRequired,
	showTask: PropTypes.func.isRequired,
	onToggleCheckedTask: PropTypes.func.isRequired,
	onMasterCheckboxToggle: PropTypes.func.isRequired,
	allTasksChecked: PropTypes.bool.isRequired,
	formFilters: PropTypes.object.isRequired,
	checkedTasks: PropTypes.array.isRequired,
	onBulkPushdownComplete: PropTypes.func.isRequired,
	onComplete: PropTypes.func.isRequired,
	hasNoFilters: PropTypes.func.isRequired,
	clearFilters: PropTypes.func.isRequired,
	onSort: PropTypes.func.isRequired,
	updateCreatedDateParams: PropTypes.func.isRequired,
	onViewSelectorChange: PropTypes.func.isRequired,
	onViewFiltersClick: PropTypes.func.isRequired,
	lastFilterChange: PropTypes.instanceOf(Date).isRequired,
	selectedView: PropTypes.string.isRequired,
	userActions: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
	return {
		userActions: bindActionCreators(userActions, dispatch),
	};
}

export default withRouter(connect(null, mapDispatchToProps)(BaselineTaskListDesktop));
